import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Print,
  FilterList,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { studentAPI, branchAPI, batchAPI, courseAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const RejectedApplications = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    status: 'rejected', // Always rejected
    centre: '',
    search: '',
    batch: '',
    course: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    fetchStudents();
    if (isSuperAdmin) {
      fetchBranches();
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchBatches();
    fetchCourses();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data.data?.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: 'rejected', // Force rejected status
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await studentAPI.getAll(params);
      setStudents(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load rejected applications');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = async (studentId) => {
    try {
      const response = await studentAPI.getOne(studentId);
      setSelectedStudent(response.data.data.student);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching student details:', error);
      toast.error('Failed to load student details');
    }
  };

  const handlePrint = (student) => {
    sessionStorage.setItem('printStudent', JSON.stringify(student));
    const printWindow = window.open('/admin/students/print', '_blank');
    if (printWindow) {
      printWindow.focus();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Rejected Applications
        </Typography>
        <Chip label={`${total} Rejected`} color="error" />
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Centre</InputLabel>
                <Select
                  name="centre"
                  value={filters.centre}
                  onChange={handleFilterChange}
                  label="Centre"
                >
                  <MenuItem value="">All Centres</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name} ({branch.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Name or Form Number"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Batch</InputLabel>
              <Select
                name="batch"
                value={filters.batch}
                onChange={handleFilterChange}
                label="Batch"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Course</InputLabel>
              <Select
                name="course"
                value={filters.course}
                onChange={handleFilterChange}
                label="Course"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name} ({c.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Form No</strong></TableCell>
                <TableCell><strong>Student Name</strong></TableCell>
                <TableCell><strong>Course</strong></TableCell>
                <TableCell><strong>Centre</strong></TableCell>
                <TableCell><strong>Rejected Date</strong></TableCell>
                <TableCell><strong>Rejection Reason</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No rejected applications found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student) => (
                  <TableRow
                    key={student._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewDetails(student._id)}
                  >
                    <TableCell>{student.formNumber || '-'}</TableCell>
                    <TableCell>{student.personalInfo?.nameAsPerTC || '-'}</TableCell>
                    <TableCell>
                      {student.course?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {student.course?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {student.centre?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {student.centre?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(student.rejectedAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="error" sx={{ maxWidth: 200 }}>
                        {student.rejectionReason || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(student._id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {user.role === 'branchadmin' && student.centre?._id === user.branch && (
                        <Tooltip title="Edit & Resubmit">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => navigate(`/admin/students/edit/${student._id}`)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Print">
                        <IconButton
                          size="small"
                          onClick={() => handlePrint(student)}
                        >
                          <Print />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedStudent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Student Application Details
                </Typography>
                <Chip label="REJECTED" color="error" size="small" />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {selectedStudent.photo && (
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/${selectedStudent.photo}`}
                        alt="Student Photo"
                      />
                    </Card>
                  </Grid>
                )}

                <Grid item xs={12} md={selectedStudent.photo ? 9 : 12}>
                  <Typography variant="subtitle2" color="text.secondary">Form Number</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedStudent.formNumber}</Typography>

                  <Typography variant="subtitle2" color="text.secondary">Course</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedStudent.course?.name} ({selectedStudent.course?.code})
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">Centre</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedStudent.centre?.name} ({selectedStudent.centre?.code})
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">Rejected Date</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatDate(selectedStudent.rejectedAt)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="error">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Rejection Reason:</Typography>
                    <Typography variant="body2">{selectedStudent.rejectionReason}</Typography>
                    {selectedStudent.remarks && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>Remarks:</Typography>
                        <Typography variant="body2">{selectedStudent.remarks}</Typography>
                      </>
                    )}
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Personal Information</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name (as per TC)</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.nameAsPerTC}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body1">{formatDate(selectedStudent.personalInfo?.dateOfBirth)}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.gender}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.phone}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.email || '-'}</Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {user.role === 'branchadmin' && selectedStudent.centre?._id === user.branch && (
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<Edit />}
                  onClick={() => {
                    setDetailsDialog(false);
                    navigate(`/admin/students/edit/${selectedStudent._id}`);
                  }}
                >
                  Edit & Resubmit
                </Button>
              )}
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default RejectedApplications;
